import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostListener, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { OidcAuthService, TenantTemplateOptionModel } from 'baseflow-auth';
import { Observable, OperatorFunction, filter, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { HttpHelpers, fade } from 'shared';

import { ValidationErrorsDirective } from '../../../directives/validation-errors.directive';
import { TWizardStepId } from '../../../interfaces';
import { TenantService } from '../../../services';
import { WizardStepBaseComponent } from '../base/wizard-step-base.component';

@Component({
    selector: 'app-wizard-step-tenant',
    templateUrl: './wizard-step-tenant.component.html',
    styleUrls: ['../base/wizard-step-base.component.scss', './wizard-step-tenant.component.scss'],
    animations: [fade()],
    imports: [FormsModule, ValidationErrorsDirective, TranslatePipe],
})
export class WizardStepTenantComponent extends WizardStepBaseComponent {
    private readonly tenantService = inject(TenantService);
    private readonly oidcAuthService = inject(OidcAuthService);

    public id: TWizardStepId = 'tenant';
    public nextButtonLabel = 'next';

    public templates: Array<TenantTemplateOptionModel> = [];
    public selectedTemplate?: TenantTemplateOptionModel;
    protected tenantCreationError?: string;
    protected loggedInUser: string;

    public constructor() {
        super();
        const oidcAuthService = this.oidcAuthService;

        oidcAuthService.user
            .pipe(
                filter(Boolean),
                tap((user) => {
                    const fullName = user.fullName?.includes('undefined') ? null : user.fullName;
                    this.loggedInUser = fullName ?? user.username;
                }),
                takeUntilDestroyed()
            )
            .subscribe();
    }

    public show(): void {
        this.getTemplates();
        super.show();
    }

    @HostListener('keyup.enter')
    public onSubmit(): void {
        this.stepSubmit.emit();
    }

    public setSelectedTemplate(): void {
        this.selectedTemplate = this.templates.find((t) => t.id === this.form.tenantTemplateId) ?? undefined;
    }

    public trackById(index: number, item: { id: string }): string {
        return item.id;
    }

    protected submitWizardStep(): Observable<boolean> {
        return this.tenantService.createTenant(this.form.tenantName).pipe(
            catchError((error) => this.handleTenantCreationError(error)),
            switchMap((tenant) => {
                if (tenant === false) return of(false);
                this.tenantCreationError = undefined;
                this.tenantService.setActiveTenant(tenant);
                return this.tenantService.updateTenant(this.form.tenantName, this.selectedTemplate.id);
            }),
            catchError((error) => this.handleTenantCreationError(error)),
            map((tenant) => tenant !== false)
        );
    }

    protected logout(): void {
        this.oidcAuthService.logout();
    }

    private handleTenantCreationError(err: HttpErrorResponse): Observable<false> {
        console.error(err);
        this.tenantCreationError = 'errors.tenantCreationError';
        return of(false);
    }

    private getTemplates(): void {
        this.tenantService
            .getTenantTemplates('en')
            .pipe(
                HttpHelpers.logError as OperatorFunction<
                    Array<TenantTemplateOptionModel>,
                    Array<TenantTemplateOptionModel>
                >,
                tap((response: Array<TenantTemplateOptionModel>) => (this.templates = response))
            )
            .subscribe();
    }
}
